import React, { useState, useEffect } from 'react';
import { useCart } from '../../contexts/CartContext';
import { useDealership } from '../../contexts/DealershipContext';
import { calculateLineItemTotals } from '../../utils/pricing';
import './CartItem.css';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatPhoneDisplay = (phone) => {
  if (!phone) return '';
  const extMatch = phone.match(/Ext\.\s*(\d+)/i);
  const digits = phone.replace(/\s*Ext\.\s*\d+/i, '').replace(/\D/g, '');
  let formatted = digits;
  if (digits.length === 10) {
    formatted = `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return extMatch ? `${formatted} Ext. ${extMatch[1]}` : formatted;
};

const CartItem = ({ item, index, employees }) => {
  const { removeFromCart, updateOrderDetails, lineItems } = useCart();
  const { getProductDetails, productDetails, productLoading } = useDealership();

  // IDs as strings for safe comparison!
  const [selectedPackage, setSelectedPackage] = useState(item.packageId ? String(item.packageId) : '');
  const [selectedVariant, setSelectedVariant] = useState(item.variantId ? String(item.variantId) : '');
  const [selectedEmployee, setSelectedEmployee] = useState(item.employeeId ? String(item.employeeId) : '');
  const [quantity, setQuantity] = useState(item.quantity || 1);

  const isCustomAd =
    item.name === "Custom Ad Request" ||
    item.name === "CustomAdRequest" ||
    item.name?.includes("Custom Ad"); 
    
  const isEmployeeProduct = !!((productDetails && productDetails.employeeInfo) || item.employeeInfo);

  // Keep props in sync when they change
  useEffect(() => {
    if (item.employeeId && Array.isArray(employees)) {
      const foundEmp = employees.find(e => Number(e.id) === Number(item.employeeId));
    if (!foundEmp) return;

    }
  }, [item, employees, index]);

  useEffect(() => {
    if (item.itemId && !isCustomAd) getProductDetails(item.itemId);
  }, [item.itemId, getProductDetails, isCustomAd]);

  useEffect(() => {
    setSelectedPackage(item.packageId ? String(item.packageId) : '');
    setSelectedVariant(item.variantId ? String(item.variantId) : '');
    setSelectedEmployee(item.employeeId ? String(item.employeeId) : '');
    setQuantity(item.quantity || 1);
  }, [item.packageId, item.variantId, item.employeeId, item.quantity]);

  const employeeList = Array.isArray(employees) ? employees : [];

  const handleEmployeeChange = (e) => {
    const newEmployeeId = e.target.value;
    setSelectedEmployee(newEmployeeId);

    const newEmployee = employeeList.find(emp => String(emp.id) === String(newEmployeeId));
    if (!newEmployee) {
      return;
    }
    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      employeeId: String(newEmployeeId),
      employeeName: newEmployee.name || '',
    };
    const orderTotal = updatedItems.reduce((t, i) => t + (Number(i.total) || 0), 0);
    updateOrderDetails({ lineItems: updatedItems, orderTotal });
  };

  const handlePackageChange = (e) => {
    const newPackageId = e.target.value;
    setSelectedPackage(newPackageId);
    const newPkg = productDetails?.packages?.find(pkg => String(pkg.id) === String(newPackageId));
    if (!newPkg) {
      return;
    }
    const price = Number(newPkg.price) || 0;
    const fee = Number(newPkg.fee) || 0;
    const pricing = calculateLineItemTotals({ price, fee, quantity });
    if (price === 0) {
      console.warn(`[CartItem] handlePackageChange: Selected package has zero price`, newPkg);
    }
    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      packageId: String(newPackageId),
      packageName: newPkg.name || '',
      price: pricing.price,
      fee: pricing.fee,
      markup: pricing.markup,
      total: pricing.total,
      quantity: pricing.quantity,
    };
    const orderTotal = updatedItems.reduce((t, i) => t + (Number(i.total) || 0), 0);
    updateOrderDetails({ lineItems: updatedItems, orderTotal });
  };

  const handleVariantChange = (e) => {
    const newVariantId = e.target.value;
    setSelectedVariant(newVariantId);
    const newVariant = productDetails?.variants?.find(v => String(v.id) === String(newVariantId));
    if (!newVariant) {
      return;
    }
    const updatedItems = [...lineItems];
    updatedItems[index] = {
      ...updatedItems[index],
      variantId: String(newVariantId),
      variantName: newVariant.name || '',
    };
    const orderTotal = updatedItems.reduce((t, i) => t + (Number(i.total) || 0), 0);
    updateOrderDetails({ lineItems: updatedItems, orderTotal });
  };


  // const handleQuantityChange = (e) => {
  //   const newQty = Math.max(1, Number(e.target.value));
  //   setQuantity(newQty);

  //   const currentPackage = productDetails?.packages?.find(pkg => String(pkg.id) === String(selectedPackage));
  //   if (!currentPackage) {
  //     console.error(`[CartItem] handleQuantityChange: No package selected or package not found`, selectedPackage, productDetails?.packages);
  //     return;
  //   }
  //   const price = Number(currentPackage?.price) || 0;
  //   const fee = Number(currentPackage?.fee) || 0;
  //   const newTotal = (price + fee) * newQty;
  //   if (price === 0) {
  //     console.warn(`[CartItem] handleQuantityChange: Current package price is zero`, currentPackage);
  //   }
  //   const updatedItems = [...lineItems];
  //   updatedItems[index] = {
  //     ...updatedItems[index],
  //     quantity: newQty,
  //     total: newTotal,
  //     price,
  //     fee,
  //   };
  //   updateOrderDetails({ lineItems: updatedItems });
  // };

  // Custom ad view (unchanged)
  const renderCustomAdDetails = () => (
    <div className="custom-ad-details">
      {item.adType && (
        <div className="custom-ad-option">
          <span className="option-label">Ad Type:</span>
          <span className="option-value">{item.adType}</span>
        </div>
      )}
      {item.adSize && (
        <div className="custom-ad-option">
          <span className="option-label">Size:</span>
          <span className="option-value">{item.adSize}</span>
        </div>
      )}
      {item.numVehicles && (
        <div className="custom-ad-option">
          <span className="option-label">Vehicles:</span>
          <span className="option-value">{item.numVehicles}</span>
        </div>
      )}
      {item.offerType && (
        <div className="custom-ad-option">
          <span className="option-label">Offer Type:</span>
          <span className="option-value">{item.offerType}</span>
        </div>
      )}
      {item.notes && (
        <div className="custom-ad-option">
          <span className="option-label">Notes:</span>
          <span className="option-value">{item.notes}</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="cart-item">
      {item.image ? (
        <img className="item-image" src={item.image} alt={item.name} />
      ) : (
        <div className="item-image">No Image</div>
      )}

      <div className="item-details">
        <h3>{item.name}</h3>

        {isCustomAd ? (
          renderCustomAdDetails()
        ) : productLoading ? (
          <div className="package-selector loading">Loading options...</div>
        ) : (
          <>
            {(productDetails?.packages?.length >= 2) && (
              <div className="package-selector">
                <label>Package: </label>
                <select
                  className="select-package"
                  value={selectedPackage || ''}
                  onChange={handlePackageChange}
                >
                  <option value="">-- Select Package --</option>
                  {productDetails.packages.map(pkg => (
                    <option key={pkg.id} value={String(pkg.id)}>
                      {pkg.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {(productDetails?.variants?.length >= 2) && (
              <div className="package-selector">
                <label>Variant: </label>
                <select
                  className="select-variant"
                  value={selectedVariant || ''}
                  onChange={handleVariantChange}
                >
                  <option value="">-- Select Variant --</option>
                  {productDetails.variants.map(variant => (
                    <option key={variant.id} value={String(variant.id)}>
                      {variant.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isEmployeeProduct && employees?.length > 0 && (
              <div className="package-selector">
                <label>Employee: </label>
                <select
                  className="select-employee"
                  value={selectedEmployee || ''}
                  onChange={handleEmployeeChange}
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={String(emp.id)}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {isEmployeeProduct && selectedEmployee && (() => {
              const emp = employeeList.find(e => String(e.id) === String(selectedEmployee));
              if (!emp) return null;
              const phoneFields = [
                { label: 'Main Phone', value: emp.mainPhone },
                { label: 'Mobile Phone', value: emp.mobilePhone },
                { label: 'Fax', value: emp.fax },
              ];
              return (
                <div className="employee-recap">
                  {emp.title && (
                    <div className="employee-recap-row">
                      <span className="recap-label">Title:</span>
                      <span className="recap-value">{emp.title}</span>
                    </div>
                  )}
                  {emp.name && (
                    <div className="employee-recap-row">
                      <span className="recap-label">Name:</span>
                      <span className="recap-value">{emp.name}</span>
                    </div>
                  )}
                  {emp.email && (
                    <div className="employee-recap-row">
                      <span className="recap-label">Email:</span>
                      <span className="recap-value">{emp.email}</span>
                    </div>
                  )}
                  {phoneFields.filter(f => f.value).map(f => (
                    <div key={f.label} className="employee-recap-row">
                      <span className="recap-label">{f.label}:</span>
                      <span className="recap-value">{formatPhoneDisplay(f.value)}</span>
                    </div>
                  ))}
                </div>
              );
            })()}

          </>
        )}

        <div className="item-actions">
          <button
            className="cart-remove-button"
            onClick={() => removeFromCart(index)}
          >
            Remove
          </button>
        </div>
      </div>

      {/* Line price — data already on the item; display only */}
      {isCustomAd ? (
        <div className="item-price item-price--custom">
          <span className="item-price-total">Prices Vary</span>
          <span className="item-price-note">
            Due to this being a Custom Creative item, MA will contact you with pricing.
          </span>
        </div>
      ) : (
        Number(item.total) > 0 && (
          <div className="item-price">
            <span className="item-price-total">{formatCurrency(item.total)}</span>
            {Number(item.quantity) > 1 && (
              <span className="item-price-qty">Qty: {item.quantity}</span>
            )}
          </div>
        )
      )}
    </div>
  );
};

export default CartItem;
